/* eslint-disable react-hooks/exhaustive-deps */
import * as React from 'react'
import { useLocation } from 'react-router-dom'

import {
  Box,
  Button,
  DescriptionType,
  IllustrationType,
} from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import Modal from 'modules/core/components/Modal'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'

import { locale } from 'modules/marketplace_modules/components/utils/locales'
import HubspotMeetingsWrapper from 'modules/onboarding/components/HelpWidget/WidgetSidepanel/HubspotMeetingsWrapper'
import { useGetProductUpdateModules } from 'modules/product_updates/hooks/useGetProductUpdateModules'
import IntroFooter from 'modules/upsell/components/RequestUpgradeModal/_IntroFooter'
import ModalDescription from 'modules/upsell/components/RequestUpgradeModal/_ModalDescription'
import defaultIcon from 'modules/upsell/components/RequestUpgradeModal/_ModalDescription/icons/default.svg'
import icon from 'modules/upsell/components/RequestUpgradeModal/_ModalDescription/icons/rocket.svg'
import UpsellModalModules from 'modules/upsell/components/UpsellModalModules'
import useCreateUpsellRequest, { ValidReasons } from 'modules/upsell/hooks/useCreateUpsellRequest'
import useMeetingData from 'modules/upsell/hooks/useMeetingData'
import type { RequestUpgradeProps } from 'modules/upsell/lib/requestUpgradeTypes'

interface BaseRequestUpgradeModalProps {
  onClose: () => void
  onSuccess?: (id?: number | null) => void
  title?: string
  description?: DescriptionType
  customSuccessDescription?: string
  pageSource?: string
}

export type RequestUpgradeModalProps = BaseRequestUpgradeModalProps & RequestUpgradeProps

export type ModalConfig = {
  illustration: IllustrationType
  title: string
  isUpsellModal: boolean
}
export interface ModalDescriptionProps {
  description?: DescriptionType
  isLoading?: boolean
  notified?: boolean
  success?: boolean
}

const getDefaultMessage = (
  requestReason: ValidReasons,
  moduleRequested?: string | null
): { title: string; description: string; icon?: string } => {
  switch (requestReason) {
    case ValidReasons.Activation:
      return {
        title: i18n.t('upsell.request_modal.activation.title'),
        description: i18n.t('upsell.request_modal.activation.description'),
        icon: icon,
      }
    case ValidReasons.MissingAddon:
      return {
        title: i18n.t('upsell.request_modal.missing_module.title', {
          module_name: moduleRequested,
        }),
        description: i18n.t('upsell.request_modal.missing_module.description'),
      }
    case ValidReasons.MissingFeature:
      return {
        title: i18n.t('upsell.request_modal.missing_feature.title'),
        description: i18n.t('upsell.request_modal.missing_feature.description'),
        icon: icon,
      }
    case ValidReasons.MissingFeatureLegacy:
      return {
        title: i18n.t('upsell.request_modal.missing_feature.title'),
        description: i18n.t('upsell.request_modal.missing_feature.description'),
        icon: icon,
      }
    case ValidReasons.UpgradeFromLegacy:
      return {
        title: i18n.t('upsell.request_modal.legacy_plan.title'),
        description: i18n.t('upsell.request_modal.legacy_plan.description'),
        icon: icon,
      }
  }
}

const RequestUpgradeModal = ({
  title,
  description,
  customSuccessDescription,
  onClose,
  onSuccess,
  requestSource,
  requestReason,
  moduleRequested,
  featureRequested,
  requestComponent,
  pageSource,
}: RequestUpgradeModalProps) => {
  const { pathname } = useLocation()
  const [showModal, setShowModal] = React.useState(true)
  const [datoError, setDatoError] = React.useState(false)
  const [submitSuccess, setSubmitSuccess] = React.useState(false)
  const [isCalendarAvailable, setIsCalendarAvailable] = React.useState(false)
  const [customDescription, setCustomDescription] = React.useState(description)
  const { create, loading, called } = useCreateUpsellRequest()
  const { meetingData, onMeetingBooked, writeData, shouldShowCalendar } = useMeetingData({
    onSuccessEvent: trackingEvents.upsell.upsellMeetingBooked,
  })
  const notified = submitSuccess || called

  const session = useCurrentSession()
  const datoFormatLocale = locale[session.access.locale]
  const { modules, loading: loadingModule } = useGetProductUpdateModules({
    moduleSlug: moduleRequested,
    locale: datoFormatLocale,
  })
  const [{ name: moduleRequestedName = '', icon: moduleIcon = '' } = {}] = modules
  const defaultMessage = getDefaultMessage(requestReason, moduleRequestedName)

  const onRequestContact = async () => {
    const meetingBooked = meetingData?.meetingBookSucceeded
    setSubmitSuccess(false)

    const result = await create({
      moduleRequested,
      requestSource,
      requestReason,
      featureRequested,
      meetingBooked,
      pageSource,
    })

    const handled = handleResult(result, () => undefined)
    if (handled.success) {
      writeData({ meetingBookSucceeded: false })
      setSubmitSuccess(true)
      setIsCalendarAvailable(false)
      onSuccess?.(handled.data?.upsellRequest?.id)
    }
  }

  const customOnClose = () => {
    onClose()
    setShowModal(false)
    setIsCalendarAvailable(false)
  }

  const getModalConfig = (): ModalConfig => {
    const result: ModalConfig = {
      illustration: 'crown',
      title: '',
      isUpsellModal: true,
    }

    if (!loading && notified) {
      result.illustration = submitSuccess ? 'crown' : 'delete-cross'
      result.title = submitSuccess
        ? i18n.t('upsell.request_modal.success_title')
        : i18n.t('packaging.upgrade_modal.retry_title')
    }

    return result
  }

  const getModalFooter = () => {
    if (isCalendarAvailable) {
      return (
        <Button onClick={() => setIsCalendarAvailable(false)} type="secondary">
          {i18n.t('upsell.go_back')}
        </Button>
      )
    } else if (notified) {
      return (
        <Button
          onClick={submitSuccess ? onClose : onRequestContact}
          type="secondary"
          disabled={loading}
        >
          {submitSuccess
            ? i18n.t('upsell.request_modal.success_button')
            : i18n.t('common.try_again')}
        </Button>
      )
    } else {
      return (
        <IntroFooter
          isLoading={loading}
          onRequestContact={onRequestContact}
          setIsCalendarAvailable={setIsCalendarAvailable}
        />
      )
    }
  }

  useEffect(() => {
    if (datoError || !moduleRequested) {
      shouldShowCalendar().then((shouldShow) => {
        track(trackingEvents.upsell.openRequestUpgradeModal, {
          module_requested: moduleRequested,
          feature_request: featureRequested,
          request_source: requestSource,
          request_reason: requestReason,
          page_path: pathname,
          page_source: pageSource,
          is_am_assigned: shouldShow,
          request_component: requestComponent,
        })
        shouldShow && setCustomDescription(i18n.t('upsell.book_a_meeting_description'))
      })
      if (isCalendarAvailable && meetingData?.meetingBookSucceeded) {
        onRequestContact()
      }
    }
  }, [meetingData?.meetingBookSucceeded])

  if (loadingModule) return null

  if (!showModal) return null
  if (moduleRequested && !featureRequested && !datoError) {
    return (
      <UpsellModalModules
        moduleName={moduleRequested}
        requestSource={requestSource}
        pageSource={pageSource}
        featureIdentifier={featureRequested}
        requestReason={requestReason}
        variant={'revamp'}
        onClose={onClose}
        onSuccess={onSuccess}
        onError={() => setDatoError(true)}
        requestComponent={requestComponent}
      />
    )
  }

  return (
    <Modal
      background="radial-gradient(50% 85.16% at 50% 0%, #FBE3BC 0%, #FFFFFF 100%)"
      {...getModalConfig()}
      description={
        !isCalendarAvailable && (
          <ModalDescription
            title={title || defaultMessage.title}
            customSuccessDescription={customSuccessDescription}
            description={customDescription || defaultMessage.description}
            icon={defaultMessage.icon || moduleIcon || defaultIcon}
            isLoading={loading}
            success={submitSuccess}
          />
        )
      }
      footer={getModalFooter()}
      onClose={customOnClose}
      locked={loading}
    >
      {isCalendarAvailable && (
        <Box height="s600">
          <HubspotMeetingsWrapper isUpsellMeeting onMeetingBooked={onMeetingBooked} />
        </Box>
      )}
    </Modal>
  )
}

export default observer(RequestUpgradeModal)
